// Frontend mirror of supabase/functions/_shared/publishing/registry.ts.
//
// Edge-function code lives in a separate runtime (Deno) and cannot be
// imported by the Vite app, so we duplicate the registry here. A unit test
// (publishingRegistry.test.ts) asserts that the destination keys, statuses,
// and unofficial flags stay in lockstep across the two files. Always update
// both in the same change.

export type PublishingDestinationKey =
  | "generic_webhook"
  | "email"
  | "kit"
  | "bento"
  | "wordpress"
  | "wordpress_self_hosted"
  | "ghost"
  | "substack_unofficial"
  | "beehiiv"
  | "buttondown";

export type PublishingFieldShape = "url" | "secret" | "string" | "email_list";

/** Mirror of PublishingSurface in the edge registry. Long-form only after
 *  2026-07-03 (short-form/micropost surface retired). */
export type PublishingSurface = "long_form";

export type PublishingDestinationDescriptor = {
  key: PublishingDestinationKey;
  label: string;
  description: string;
  status: "live" | "reserved";
  unofficial: boolean;
  surfaces: ReadonlyArray<PublishingSurface>;
  capabilities: {
    supports_test: boolean;
    supports_html_body: boolean;
    supports_markdown_body: boolean;
    supports_scheduling: boolean;
    requires_secret: boolean;
    multi_recipient: boolean;
    /** Optional. Hard cap on body characters — currently only set for
     *  Substack Notes (~280 chars). When omitted there is no enforced
     *  limit. UI uses this for live char-count + truncation warning;
     *  adapter truncates with ellipsis on dispatch. */
    max_chars?: number;
  };
  fields: ReadonlyArray<{
    name: string;
    label: string;
    shape: PublishingFieldShape;
    required: boolean;
    placeholder?: string;
  }>;
};

export const PUBLISHING_REGISTRY: Readonly<
  Record<PublishingDestinationKey, PublishingDestinationDescriptor>
> = {
  generic_webhook: {
    key: "generic_webhook",
    label: "Generic webhook",
    description: "POST your prepared content as JSON to any HTTPS endpoint.",
    status: "live",
    unofficial: false,
    surfaces: ["long_form"],
    capabilities: {
      supports_test: true,
      supports_html_body: true,
      supports_markdown_body: true,
      supports_scheduling: false,
      requires_secret: false,
      multi_recipient: false,
    },
    fields: [
      { name: "target_url", label: "Target URL", shape: "url", required: true, placeholder: "https://example.com/hook" },
      { name: "auth_header_name", label: "Auth header name", shape: "string", required: false, placeholder: "Authorization" },
      { name: "secret_value", label: "Auth header value", shape: "secret", required: false },
    ],
  },
  email: {
    key: "email",
    label: "Email",
    description: "Send the prepared content as a transactional email to a fixed recipient list.",
    status: "live",
    unofficial: false,
    surfaces: ["long_form"],
    capabilities: {
      supports_test: true,
      supports_html_body: true,
      supports_markdown_body: false,
      supports_scheduling: false,
      requires_secret: false,
      multi_recipient: true,
    },
    fields: [
      { name: "recipients", label: "Recipients", shape: "email_list", required: true },
      { name: "from_name", label: "From name", shape: "string", required: false },
      { name: "reply_to", label: "Reply-to", shape: "string", required: false },
      { name: "subject_template", label: "Subject template", shape: "string", required: false, placeholder: "{{title}}" },
    ],
  },
  kit: {
    key: "kit",
    label: "Kit",
    description: "Publish broadcasts to your Kit (formerly ConvertKit) account as drafts.",
    status: "live",
    unofficial: false,
    surfaces: ["long_form"],
    capabilities: {
      supports_test: true,
      supports_html_body: true,
      supports_markdown_body: false,
      supports_scheduling: false,
      requires_secret: true,
      multi_recipient: false,
    },
    fields: [
      { name: "secret_value", label: "Kit API secret (V4)", shape: "secret", required: true },
    ],
  },
  bento: {
    key: "bento",
    label: "Bento",
    description: "Publish broadcasts to your Bento (bentonow.com) account as drafts.",
    status: "live",
    unofficial: false,
    surfaces: ["long_form"],
    capabilities: {
      supports_test: true,
      supports_html_body: true,
      supports_markdown_body: false,
      // Bento supports `send_at` scheduling but our v1 adapter only creates
      // drafts. Flip this when scheduling is wired end-to-end.
      supports_scheduling: false,
      requires_secret: true,
      multi_recipient: false,
    },
    fields: [
      { name: "site_uuid", label: "Bento site UUID", shape: "string", required: true },
      { name: "publishable_key", label: "Bento publishable key", shape: "string", required: true },
      { name: "secret_value", label: "Bento secret key", shape: "secret", required: true },
    ],
  },
  wordpress: {
    key: "wordpress",
    label: "WordPress.com",
    description:
      "Create draft posts on a WordPress.com site you've linked through the Lovable connector. The Excerptus team handles auth and token refresh — no app password needed.",
    status: "live",
    unofficial: false,
    surfaces: ["long_form"],
    capabilities: {
      supports_test: true,
      supports_html_body: true,
      supports_markdown_body: false,
      supports_scheduling: false,
      requires_secret: false,
      multi_recipient: false,
    },
    fields: [
      {
        name: "site",
        label: "WordPress.com site",
        shape: "string",
        required: true,
        placeholder: "myblog.wordpress.com",
      },
    ],
  },
  wordpress_self_hosted: {
    key: "wordpress_self_hosted",
    label: "WordPress (self-hosted)",
    description:
      "Create draft posts on your own self-hosted WordPress site using an Application Password generated in wp-admin. Drafts only — you review and publish from wp-admin.",
    status: "live",
    unofficial: false,
    surfaces: ["long_form"],
    capabilities: {
      supports_test: true,
      supports_html_body: true,
      supports_markdown_body: false,
      supports_scheduling: false,
      requires_secret: true,
      multi_recipient: false,
    },
    fields: [
      {
        name: "site_url",
        label: "WordPress site URL",
        shape: "url",
        required: true,
        placeholder: "https://yourblog.com",
      },
      {
        name: "username",
        label: "WordPress username",
        shape: "string",
        required: true,
        placeholder: "your-wp-username",
      },
      {
        name: "secret_value",
        label: "Application Password",
        shape: "secret",
        required: true,
        placeholder: "xxxx xxxx xxxx xxxx xxxx xxxx",
      },
    ],
  },
  ghost: {
    key: "ghost",
    label: "Ghost",
    description:
      "Create draft posts on a Ghost blog (Ghost Pro or self-hosted). Uses your blog's Admin API key — Excerptus signs short-lived JWTs and never sends until you publish from Ghost.",
    status: "live",
    unofficial: false,
    surfaces: ["long_form"],
    capabilities: {
      supports_test: true,
      supports_html_body: true,
      supports_markdown_body: false,
      supports_scheduling: false,
      requires_secret: true,
      multi_recipient: false,
    },
    fields: [
      {
        name: "admin_url",
        label: "Ghost admin URL",
        shape: "url",
        required: true,
        placeholder: "https://blog.example.com",
      },
      {
        name: "secret_value",
        label: "Ghost admin API key",
        shape: "secret",
        required: true,
        placeholder: "65cd7e…:b1946a…",
      },
    ],
  },
  substack_unofficial: {
    key: "substack_unofficial",
    label: "Substack (unofficial)",
    description:
      "Best-effort draft push to Substack using your signed-in browser session cookie. Substack does not provide an official API; this is unofficial and may break without notice.",
    status: "live",
    unofficial: true,
    surfaces: ["long_form"],
    capabilities: {
      supports_test: true,
      supports_html_body: true,
      supports_markdown_body: false,
      supports_scheduling: false,
      requires_secret: true,
      multi_recipient: false,
      // Notes are ~280-char guidance; retained cap even though we no longer
      // publish short-form so the field-shape docs stay consistent.
      max_chars: 280,
    },
    fields: [
      { name: "publication_url", label: "Publication URL", shape: "url", required: true, placeholder: "https://yourname.substack.com" },
      { name: "secret_value", label: "Session cookie", shape: "secret", required: true, placeholder: "substack.sid=..." },
    ],
  },
  beehiiv: {
    key: "beehiiv",
    label: "Beehiiv",
    description:
      "Create draft posts in your Beehiiv publication using a V2 API key. Drafts only — you review and publish from Beehiiv. Embedded Excerptus-hosted images are replaced with a publish checklist (Beehiiv has no public image upload endpoint).",
    status: "live",
    unofficial: false,
    surfaces: ["long_form"],
    capabilities: {
      supports_test: true,
      supports_html_body: true,
      supports_markdown_body: false,
      supports_scheduling: false,
      requires_secret: true,
      multi_recipient: false,
    },
    fields: [
      { name: "publication_id", label: "Publication ID", shape: "string", required: true, placeholder: "pub_xxxxxxxx" },
      { name: "secret_value", label: "Beehiiv API key (V2)", shape: "secret", required: true },
    ],
  },
  buttondown: {
    key: "buttondown",
    label: "Buttondown",
    description:
      "Create draft emails in your Buttondown account using an API key. Drafts only — you review and send from Buttondown. Embedded Excerptus-hosted images are replaced with a publish checklist.",
    status: "live",
    unofficial: false,
    surfaces: ["long_form"],
    capabilities: {
      supports_test: true,
      supports_html_body: true,
      supports_markdown_body: false,
      supports_scheduling: false,
      requires_secret: true,
      multi_recipient: false,
    },
    fields: [
      { name: "secret_value", label: "Buttondown API key", shape: "secret", required: true },
    ],
  },
} as const;

export const PUBLISHING_DESTINATION_KEYS: ReadonlyArray<PublishingDestinationKey> = Object.keys(
  PUBLISHING_REGISTRY,
) as PublishingDestinationKey[];

export const isLiveDestination = (key: string): key is PublishingDestinationKey => {
  if (!(key in PUBLISHING_REGISTRY)) return false;
  return PUBLISHING_REGISTRY[key as PublishingDestinationKey].status === "live";
};

export const isKnownDestination = (key: string): key is PublishingDestinationKey =>
  key in PUBLISHING_REGISTRY;

export const getDestination = (key: PublishingDestinationKey): PublishingDestinationDescriptor =>
  PUBLISHING_REGISTRY[key];

/** Convenience for pickers — only destinations users can actually use today. */
export const liveDestinations = (): PublishingDestinationDescriptor[] =>
  PUBLISHING_DESTINATION_KEYS.map((k) => PUBLISHING_REGISTRY[k]).filter((d) => d.status === "live");

/** Live destinations that accept content from a given composer surface. */
export const liveDestinationsForSurface = (
  surface: PublishingSurface,
): PublishingDestinationDescriptor[] =>
  liveDestinations().filter((d) => d.surfaces.includes(surface));

/** True if this destination accepts content from a given composer surface. */
export const supportsSurface = (
  key: PublishingDestinationKey,
  surface: PublishingSurface,
): boolean => PUBLISHING_REGISTRY[key].surfaces.includes(surface);

